import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router";
import App, { ErrorPage } from "./App";
import Home, { homeLoader } from "./pages/Home";
import Juzs, { juzsLoader } from "./pages/Juzs";
import Surahs, { surahsLoader } from "./pages/Surahs";
import Login, { loginLoader } from "./pages/Login";
import Admin, { adminAction, adminLoader } from "./pages/Admin";
import QuranPage, { quranPageLoader, useReaderThemeColor } from "./pages/QuranPage";
import WordEditor, { wordEditorAction, wordEditorLoader } from "./pages/WordEditor";
import QuranPageNav from "./components/QuranPageNav";
import QuranPagePagination from "./components/QuranPagePagination";
import QuranPageSkeleton from "./components/QuranPageSkeleton";
import { juzForPage } from "./lib/juz";
import { isValidPage } from "./lib/pages";

/** The reader's shell with the page skeleton, so the first paint is the page frame. */
function ReaderFallback({ pageNumber }: { pageNumber: number }) {
	useReaderThemeColor();
	return (
		<main className="flex min-h-svh flex-col bg-paper-100 md:bg-paper-200 print:min-h-0 print:bg-white">
			<QuranPageNav video={null} pageNumber={pageNumber} juz={juzForPage(pageNumber)} />
			<section className="mushaf-scope w-full flex-1 px-3 py-4 md:py-8">
				<QuranPageSkeleton pageNumber={pageNumber} />
			</section>
			<QuranPagePagination pageNumber={pageNumber} />
		</main>
	);
}

/** Emerald hero band and card placeholders, matching the homepage layout. */
function HomeFallback() {
	return (
		<div aria-busy="true" className="min-h-svh bg-paper-50">
			<div className="h-64 bg-emerald-950 bg-girih" />
			<div className="mx-auto grid max-w-5xl grid-cols-1 gap-3 px-4 py-6 animate-skeleton-in sm:grid-cols-2 sm:px-6 lg:grid-cols-3">
				{[0, 1, 2].map((i) => (
					<div
						key={i}
						className="h-28 rounded-card bg-paper-100 ring-1 ring-paper-300 motion-safe:animate-pulse"
					/>
				))}
			</div>
		</div>
	);
}

// Painted while the first route's loader runs, so the first paint is never blank.
function HydrateFallback() {
	const { pathname } = window.location;
	const match = pathname.match(/^\/(\d{1,3})(\/|$)/);
	const page = match ? Number(match[1]) : 0;
	if (isValidPage(page)) return <ReaderFallback pageNumber={page} />;
	// Home, Džuzevi and Sure share the emerald hero; Login and Admin do not.
	if (/^\/(juzs|surahs)?\/?$/.test(pathname)) return <HomeFallback />;
	return <div className="min-h-svh bg-paper-50" />;
}

const router = createBrowserRouter([
	{
		element: <App />,
		errorElement: <ErrorPage />,
		HydrateFallback,
		children: [
			{ index: true, element: <Home />, loader: homeLoader },
			{ path: "juzs", element: <Juzs />, loader: juzsLoader },
			{ path: "surahs", element: <Surahs />, loader: surahsLoader },
			{ path: "login", element: <Login />, loader: loginLoader },
			{ path: "admin", element: <Admin />, loader: adminLoader, action: adminAction },
			{
				path: ":pageNumber",
				element: <QuranPage />,
				loader: quranPageLoader,
				// Opening/closing the word editor must not refetch the page.
				shouldRevalidate: ({ currentParams, nextParams, formMethod }) =>
					Boolean(formMethod) || currentParams.pageNumber !== nextParams.pageNumber,
				children: [
					{
						path: "edit",
						element: <WordEditor />,
						loader: wordEditorLoader,
						action: wordEditorAction,
					},
				],
			},
		],
	},
]);

createRoot(document.getElementById("root")!).render(
	<StrictMode>
		<RouterProvider router={router} />
	</StrictMode>,
);
